import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";

// Single source of the employee's own pending requests. Previously the Home
// dashboard fanned out to ~7 endpoints to build these counts, and did it TWICE
// (Pending Requests KPI + My Requests Summary, and again for the Action Centre
// strip) — ~14 round-trips. Everything now comes from the one consolidated
// backend endpoint:
//   My_Zone/Apply/Over_time/overall-count/?user_id=<id>
//
// Shape returned by the endpoint:
//   {
//     counts_by_type: { leave, overtime, offDayDuty, workAdjustment, advanceSalary },
//     total,
//     items_by_type: { <type>: [{ id, title, subtitle, status }, ...] }
//   }
const HomeOverallCountContext = createContext(null);

const EMPTY = { countsByType: {}, itemsByType: {}, total: 0, loading: true };

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const objectHasValues = (value) =>
  value && typeof value === "object" && Object.keys(value).length > 0;

// Core fetch hook. `enabled` lets a consumer that already reads the shared
// provider value skip its own network call (see useHomeOverallCount below).
const useOverallCountFetch = (enabled) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const fetchCounts = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const res = await customGetAPI(
          `${domain}My_Zone/Apply/Over_time/overall-count/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
        );
        const body = res?.responseDetails || {};
        const countsByType = body.counts_by_type || {};
        const itemsByType = body.items_by_type || {};
        const countTotal = objectHasValues(countsByType)
          ? Object.values(countsByType).reduce(
              (total, count) => total + toNumber(count),
              0,
            )
          : null;
        const itemsTotal = objectHasValues(itemsByType)
          ? Object.values(itemsByType).reduce(
              (total, rows) => total + (Array.isArray(rows) ? rows.length : 0),
              0,
            )
          : null;
        if (cancelled) return;
        setState({
          countsByType,
          itemsByType,
          total: countTotal ?? itemsTotal ?? toNumber(body.total),
          loading: false,
        });
      } catch (error) {
        console.error("[HomeOverallCount] failed to load:", error);
        if (!cancelled) setState({ ...EMPTY, loading: false });
      }
    };
    if (userDetails?.id) fetchCounts();
    else setState({ ...EMPTY, loading: false });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, userDetails?.id, enabled, requestCompanyId]);

  return state;
};

// Wrap the Home dashboard in this so every pending-count consumer on the page
// (Pending Requests KPI, My Requests Summary, Action Centre) shares ONE fetch.
export const HomeOverallCountProvider = ({ children }) => {
  const value = useOverallCountFetch(true);
  return (
    <HomeOverallCountContext.Provider value={value}>
      {children}
    </HomeOverallCountContext.Provider>
  );
};

// Consumer hook. Under the provider (Home dashboard) it reuses the single shared
// fetch; used standalone elsewhere (e.g. My Zone > Apply pages) it fetches on
// its own, so it still costs one consolidated request instead of the old fan-out.
export const useHomeOverallCount = () => {
  const ctx = useContext(HomeOverallCountContext);
  const standalone = useOverallCountFetch(ctx == null);
  return ctx || standalone;
};
